import { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/Button";
import addCircleImage from "./../../../assets/_AddCircleIcon_.svg";
import MuiButton from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import vectorIcon from "../../../assets/_VectorIcon_.svg";
import React from "react";
import usePermission from "../../../hooks/usePermission";
import { FeatureStatic } from "../../../@types/statics/feature.static";
import { IUser } from "../../../@types/user.type";
import { searchUsers } from "../../../services/api/user/UserService";
import secureLocalStorage from "react-secure-storage";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import {
  ColDef,
  FilterChangedEvent,
  GridReadyEvent,
  IGetRowsParams,
} from "ag-grid-community";
import { IUserFilter } from "../../../@types/user-filter.type";
import { IGrid } from "../../../@types/grid.type";
import styles from "./styles.module.scss";

const formatBoolean = (value: boolean | undefined): string =>
  value ? "Sim" : "Não";

const columnsDefault: ColDef[] = [
  {
    field: "name",
    headerName: "Nome",
    flex: 1,
    minWidth: 300,
  },
  {
    field: "email",
    headerName: "Email",
    flex: 1,
    minWidth: 300,
  },
  {
    field: "lastAccess",
    headerName: "Último Acesso",
    flex: 1,
    minWidth: 200,
    filter: false,
    sortable: false,
    cellRenderer: (params: CustomCellRendererProps) => {
      const lastAccess = params.data?.lastAccess;

      if (!lastAccess) {
        return "";
      }

      const date = new Date(lastAccess);
      return date.toLocaleString("pt-BR", {
        dateStyle: "short",
        timeStyle: "medium",
      });
    },
  },
  {
    field: "isResource",
    headerName: "É um Colaborador?",
    flex: 1,
    minWidth: 200,
    cellRenderer: (params: CustomCellRendererProps) => (
      <span>{formatBoolean(params.data?.isResource)}</span>
    ),
  },
];

export const Users: React.FC = () => {
  const [columns, setColumns] = useState(columnsDefault);
  const [loading, setLoading] = useState<boolean>(true);
  const gridRef = useRef<AgGridReact<any>>(null);

  const permissions = usePermission(FeatureStatic.permissions.id);

  const navigate = useNavigate();

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersUsers")
  );
  const [cachedSort, setCachedSort] = useState(getFromCache("agGridSortUsers"));

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersUsers", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortUsers", sortState);
  };

  const onGridReady = (params: GridReadyEvent) => {
    const gridApi = params.api;

    // Restaurar filtros
    if (cachedFilters) {
      gridApi.setFilterModel(cachedFilters);
    }

    // Restaurar ordenação
    if (cachedSort) {
      gridApi.applyColumnState({
        state: cachedSort,
        defaultState: { sort: null },
      });
    }
  };

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }
  }, []);

  function navigateToUpdatePage(id: string) {
    navigate(`update/${id}`);
  }

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      console.log(params);

      const sortModel = params.sortModel;
      const filterModel = params.filterModel;

      const filter = {
        skip: params.startRow, // Linha inicial para paginação
        take: params.endRow - params.startRow, // Quantidade de linhas
      } as IUserFilter;

      if (sortModel.length > 0) {
        filter.orderByField = sortModel[0].colId;
        filter.orderByDirection = sortModel[0].sort == "asc" ? 0 : 1;
      }

      if (filterModel.name) {
        filter.name = filterModel.name.filter;
      }

      if (filterModel.email) {
        filter.email = filterModel.email.filter;
      }

      if (filterModel.isResource) {
        filter.isResource =
          String(filterModel.isResource.filter).toLowerCase() == "sim";
      }

      try {
        const response = (await searchUsers(filter)) as IGrid<IUser>;

        params.successCallback(response.items, response.totalRows);
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <div className={styles.usersContainer}>
      <div className={styles.back}>
        <MuiButton
          className={styles.buttonMui}
          onClick={() => navigate("/configurations")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </MuiButton>
      </div>
      <div className={styles.header}>
        <h1>Usuários</h1>
        <div className={styles.btnsFilterAndCreate}>
          <Button
            iconUrl={addCircleImage}
            onClick={() => {
              navigate("/configurations/users/create");
            }}
            content="Novo Usuário"
            className={styles.newUserButton}
          />
        </div>
      </div>
      <div className={`${styles.dataGridContainer}`}>
        <AgGridReact
          ref={gridRef}
          theme={themeGrid}
          localeText={localeText}
          loading={loading}
          columnDefs={columns}
          overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
          defaultColDef={{
            sortable: true,
            editable: false,
            filter: true,
            floatingFilter: true,
          }}
          suppressCellFocus={true}
          rowStyle={{ cursor: "pointer" }}
          onRowClicked={(row) => navigateToUpdatePage(row.data.id)}
          cacheBlockSize={10}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          rowModelType="infinite"
          datasource={getDataSource()}
          animateRows={true}
          onGridReady={onGridReady}
        />
      </div>
    </div>
  );
};
